import { Box, Button, CircularProgress, Grid, useMediaQuery, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { AxiosResponse } from 'axios';
import Checkbox from 'components/checkbox';
import DatePicker from 'components/date-picker';
import Dialog from 'components/dialog';
import List, { HeadCell } from 'components/list/list';
import ListCell from 'components/list/list-cell';
import ListRow from 'components/list/list-row';
import LoadingSpinner from 'components/loading-spinner';
import Pagination from 'components/pagination';

import RadioSelectItem from 'components/radio-select-item';
import Select from 'components/select';
import TabPanel from 'components/tab-panel';
import TextField from 'components/text-field';
import Tooltip from 'components/tooltip';
import { useAccountState } from 'contexts/account/account-context';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import { AlertTriangle, CheckCircle, Search } from 'lucide-react';
import Mui from 'materials';
import React, { useEffect, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { FormattedDate, FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { generatePath, useHistory, useRouteMatch } from 'react-router';
import { OwnerType } from 'types/shared/owner-type.enum';
import { CreateVirtualFishGroupInput } from 'views/planning-prognosis/features/view-fish-group/types/create-virtual-fish-group-input.type';
import { DefaultProfileType } from 'views/planning-prognosis/features/view-fish-group/types/default-profile-type.enum';
import { FishGroup as FishGroupModel } from 'views/planning-prognosis/features/view-fish-group/types/fish-group.type';

import FlexBox from 'components/flex-box';
import Paper from 'components/paper';
import Switch from 'components/switch';
import { useGet } from 'hooks/use-get';
import { usePost } from 'hooks/use-post';
import { usePagination } from 'hooks/use-pagination';
import _, { isEmpty } from 'underscore';
import { getProfilesAssortment } from 'views/planning-prognosis/api/get-profiles';
import { getFishGroups } from 'views/planning-prognosis/api/get-site-fish-groups';
import { getHarvestedFishGroups } from 'views/planning-prognosis/features/view-fish-group/api/get-harvested-fish-groups';
import EditSiteProfiles from '../../../../profiles-strategies/features/site-profiles/components/edit-site-profiles';
import { resetDefaultFishGroupProfiles } from '../../view-fish-group/api/fish-group-planning/reset-default-fish-group-profiles';
import { putDefaultFishGroupProfiles } from '../../view-fish-group/api/fish-group-planning/update-default-fish-group-profiles';
import { getSmoltType } from '../../view-fish-group/api/fish-group-stocking/get-smolt-types';
import { getFishGroupSpecies } from '../../view-fish-group/api/get-fish-group-species';
import { getFishGroupStrains } from '../../view-fish-group/api/get-fish-group-strains';
import { postVirtualFishGroup } from '../../view-fish-group/api/virtual-fish-group/create-virtual-fish-group';
import Calculator from './calculator/calculator';
import FishGroupSubTabs from './fish-group-sub-tabs';
import { postCalculation } from 'views/planning-prognosis/api/create-calculation';

const ActionGroup = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  flexDirection: 'column',
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
  },
}));

type FormValues = {
  useEgiId: boolean;
  egiId: number;
  useEfiId: boolean;
  efiId: number;
  useMortalityId: boolean;
  mortalityId: number;
  useFeedStrategyId: boolean;
  feedStrategyId: number;
};

type ParamTypes = {
  localSiteId: string;
  siteTab: string;
  fishGroupTab: Tabs;
};

enum Tabs {
  FISH_GROUPS = 'fishgroups',
  VIRTUAL = 'virtual',
  HARVESTED = 'harvested',
  PROFILES = 'profiles',
  CALCULATOR = 'calculator',
}

type Props = {
  globalSiteId: string;
  localSiteId: string;
};

const FishGroup: React.FC<Props> = ({ globalSiteId, localSiteId }) => {
  const intl = useIntl();
  const history = useHistory();
  const { params, path } = useRouteMatch<ParamTypes>();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const snackbarDispatch = useSnackbarDispatch();
  const { account } = useAccountState();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const profilesDialogRef = useRef<React.ElementRef<typeof Dialog>[]>([]);
  const createVirtualDialogRef = useRef<React.ElementRef<typeof Dialog>>(null);

  const PAGE_NAME_ID = 'planningPrognosis.viewSite.fishGroupList';
  const PAGINATION_LIMIT = 10;

  const fishGroupsListOptions: HeadCell[] = [
    { id: 'checkbox', label: '', autoWidth: true },
    { id: 'fishGroupName', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.fishGroupNameLabel` }) },
    { id: 'unitName', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.unitNameLabel` }) },
    { id: 'smoltName', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.smoltNameLabel` }) },
    { id: 'stockingDate', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.stockingDateLabel` }), align: 'center' },
    {
      id: 'stockingWeight',
      label: intl.formatMessage({ id: `${PAGE_NAME_ID}.stockingWeightLabel` }),
      align: 'right',
      sx: { whiteSpace: 'nowrap' },
    },
    {
      id: 'lastBiom',
      label: intl.formatMessage({ id: `${PAGE_NAME_ID}.currentWeightLabel` }),
      align: 'right',
      sx: { whiteSpace: 'nowrap' },
    },
    { id: 'accEgi', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.accEgiLabel` }), align: 'right' },
    { id: 'accEfi', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.accEfiLabel` }), align: 'right' },
    {
      id: 'hasTemporary',
      label: intl.formatMessage({ id: `${PAGE_NAME_ID}.hasTemporaryLabel` }),
      align: 'center',
      autoWidth: true,
    },
    { id: 'recalculating', label: 'Calc.', align: 'center', autoWidth: true },
    { id: 'actions', label: '', align: 'center', autoWidth: true },
  ];

  // #region STATE
  const { currentPage, from, to, handlePagination } = usePagination(PAGINATION_LIMIT);
  const [searchValue, setSearchValue] = useState('');
  const [selectedFishGroups, setSelectedFishGroups] = useState<string[]>([]);
  const [rows, setRows] = useState<FishGroupModel[]>([]);

  // #endregion STATE

  // #region FORMS

  const { handleSubmit, control, setValue, watch } = useForm<FormValues>({
    defaultValues: {
      useEgiId: false,
      useEfiId: false,
      useFeedStrategyId: false,
      useMortalityId: false,
      egiId: -1,
      efiId: -1,
      feedStrategyId: -1,
      mortalityId: -1,
    },
    shouldUnregister: true,
  });

  const values = useWatch({
    control: control,
  });

  const {
    handleSubmit: handleSubmitVFG,
    control: controlVFG,
    setValue: setValueVFG,
  } = useForm<CreateVirtualFishGroupInput>({
    defaultValues: {
      stockingDate: '',
      name: '',
      numberOfStockedFish: 0,
      averageStockingWeight: 0,
      speciesId: 0,
      smoltTypeId: 0,
      seaCloudStrainId: 0,
    },
    shouldUnregister: true,
  });

  const valuesVFG = useWatch({
    control: controlVFG,
  });

  // #endregion FORMS

  // #region FETCH DATA

  const { data: fishGroups, isLoading: isLoadingFishGroups } = useGet(
    ['getFishGroups', globalSiteId],
    (context) => getFishGroups(String(context.queryKey[1])),
    {
      enabled: !!globalSiteId,
    }
  );

  const { data: harvested, isLoading: isLoadingHarvested } = useGet(
    ['getHarvestedFishGroups', globalSiteId],
    (context) => getHarvestedFishGroups(String(context.queryKey[1])),
    {
      enabled: !!globalSiteId,
    }
  );

  const { data: profiles } = useGet(
    ['getProfilesAssortment', account.company?.marketId, account.company?.companyId],
    ({ queryKey }) => getProfilesAssortment(Number(queryKey[1]), String(queryKey[2])),
    {
      enabled: !!account.company?.marketId && !!account.company?.companyId,
    }
  );

  const { data: species } = useGet(
    ['getFishGroupSpecies', account.company?.marketId],
    ({ queryKey }) => getFishGroupSpecies(Number(queryKey[1])),
    {
      enabled: !!account.company?.marketId,
    }
  );

  const { data: smoltTypes } = useGet(
    ['getSmoltType', account.company?.marketId],
    ({ queryKey }) => getSmoltType(Number(queryKey[1])),
    {
      enabled: !!account.company?.marketId,
    }
  );

  const { data: strains } = useGet(
    ['getFishGroupStrains', account.company?.marketId],
    ({ queryKey }) => getFishGroupStrains(Number(queryKey[1])),
    {
      enabled: !!account.company?.marketId,
    }
  );

  // #endregion GET DATA

  // #region POST DATA

  const startCalculation = usePost<AxiosResponse, { owner: OwnerType; id: string }>(
    (request) => postCalculation(request.owner, request.id),
    () => {
      queryClient.invalidateQueries({
        queryKey: ['getSiteCalculationStatus'],
      });
    }
  );

  const updateDefaultProfile = usePost<
    { index: number; type: DefaultProfileType },
    { fishGroupIds: string[]; type: DefaultProfileType; profileId: number }
  >(
    (request) => putDefaultFishGroupProfiles(request.fishGroupIds, request.type, request.profileId, 0),
    (response) => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
      if (response.type === DefaultProfileType.FEED_STRATEGY) {
        startCalculation.mutate({ owner: OwnerType.SITE, id: localSiteId });
      }
      queryClient.invalidateQueries({
        queryKey: ['getFishGroups'],
      });
      profilesDialogRef.current.forEach((dialog) => dialog.setOpen(false));
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  const createVirtualFishgroup = usePost<AxiosResponse, CreateVirtualFishGroupInput>(
    (request) => postVirtualFishGroup(localSiteId, request),
    () => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
      createVirtualDialogRef.current?.setOpen(false);
      queryClient.invalidateQueries({
        queryKey: ['getFishGroups'],
      });
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  const resetDefaultProfiles = usePost<number, { fishGroupIds: string[]; owner: OwnerType }>(
    (request) => resetDefaultFishGroupProfiles(OwnerType.FISH_GROUP, request.fishGroupIds, 0),
    () => {
      [['getFishGroup'], ['getDefaultFishGroupProfiles'], ['getFishGroupCalculationStatus']].forEach((queryKey) => {
        queryClient.invalidateQueries({ queryKey });
      });
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
      profilesDialogRef.current.forEach((dialog) => dialog.setOpen(false));
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  // #endregion POST DATA

  // #region HANDLERS
  const splitFishGroups = selectedFishGroups.map((fishGroupId_IDX) => fishGroupId_IDX.split('_')[0]);

  const checkMultiple = () => {
    const selected = (fishGroups && fishGroups?.filter((f) => splitFishGroups.includes(f.fishGroupId))) || [];

    let isSameEGI = selected?.every((f, i, a) => f.profileEgi === a[0].profileEgi);
    let isSameEFI = selected?.every((f, i, a) => f.profileEfi === a[0].profileEfi);
    let isSameMortality = selected?.every((f, i, a) => f.profileMort === a[0].profileMort);
    let isSameFeedStrategy = selected?.every((f, i, a) => f.profileFeedStrategy === a[0].profileFeedStrategy);

    setValue('egiId', isSameEGI && !_.isEmpty(selected) ? selected[0].profileEgi : -1);
    setValue('efiId', isSameEFI && !_.isEmpty(selected) ? selected[0].profileEfi : -1);
    setValue('mortalityId', isSameMortality && !_.isEmpty(selected) ? selected[0].profileMort : -1);
    setValue('feedStrategyId', isSameFeedStrategy && !_.isEmpty(selected) ? selected[0].profileFeedStrategy : -1);

    setValue('useEgiId', isSameEGI);
    setValue('useEfiId', isSameEFI);
    setValue('useMortalityId', isSameMortality);
    setValue('useFeedStrategyId', isSameFeedStrategy);
  };

  const handleResetProfiles = () => {
    resetDefaultProfiles.mutate({ fishGroupIds: splitFishGroups, owner: OwnerType.FISH_GROUP });
  };

  const handleOnClick = (url: string) => {
    history.push(url);
  };

  const handleChangeTab = (event: React.ChangeEvent<{}>, newValue: Tabs) => {
    handlePagination(1);
    Object.assign(params, { fishGroupTab: newValue });
    history.push(generatePath(path, params));
  };

  const handleFishGroups = (value: string, checked: boolean) => {
    setSelectedFishGroups(checked ? selectedFishGroups.concat([value]) : selectedFishGroups.filter((s) => s !== value));
  };

  const handleSubmitProfile = (data: FormValues) => {
    if (data.useEgiId) {
      updateDefaultProfile.mutate({
        fishGroupIds: splitFishGroups,
        type: DefaultProfileType.EGI,
        profileId: data.egiId,
      });
    }
    if (data.useEfiId) {
      updateDefaultProfile.mutate({
        fishGroupIds: splitFishGroups,
        type: DefaultProfileType.EFI,
        profileId: data.efiId,
      });
    }
    if (data.useMortalityId) {
      updateDefaultProfile.mutate({
        fishGroupIds: splitFishGroups,
        type: DefaultProfileType.MORTALITY,
        profileId: data.mortalityId,
      });
    }
    if (data.useFeedStrategyId) {
      updateDefaultProfile.mutate({
        fishGroupIds: splitFishGroups,
        type: DefaultProfileType.FEED_STRATEGY,
        profileId: data.feedStrategyId,
      });
    }
  };

  // #endregion HANDLERs

  // #region RENDERS
  const renderRows = () => {
    return _.chain(rows)
      .filter((fg) =>
        isEmpty(searchValue)
          ? true
          : fg.lastFishGroupIdFromXml.toLowerCase().includes(searchValue.toLowerCase()) ||
            fg.fishGroupName.toLowerCase().includes(searchValue.toLowerCase()) ||
            fg.unitName.toLowerCase().includes(searchValue.toLowerCase())
      )
      .value();
  };

  const renderRecalculating = (fishGroup: FishGroupModel) => {
    if (fishGroup.recalculating) return <CircularProgress size={18} />;
    if (fishGroup.recalculationNeeded)
      return (
        <Tooltip arrow title={intl.formatMessage({ id: 'general.recalculateIsNeeded' })}>
          <AlertTriangle fill={theme.palette.warning.light} color={theme.palette.darkBlue.main} size={18} style={{ marginTop: 4 }} />
        </Tooltip>
      );
    return <></>;
  };

  const renderFishgroup = (row: FishGroupModel, idx: number, tab: Tabs) => {
    return (
      <ListRow
        key={idx}
        onClick={() => handleOnClick(`/planning-prognosis/sites/${params.siteTab}/${params.localSiteId}/${tab}/${row.fishGroupId}`)}>
        <ListCell align="center" sx={{ width: { xs: '100%', md: 'auto' } }}>
          <Checkbox
            dark
            hiddenLabel
            tabIndex={-1}
            checked={selectedFishGroups.includes(row.fishGroupId)}
            onChange={(_, checked) => handleFishGroups(row.fishGroupId, checked)}
          />
        </ListCell>
        <ListCell align="left">{row.fishGroupName}</ListCell>
        <ListCell align="left">{row.unitName}</ListCell>
        <ListCell align="left">{row.smoltName}</ListCell>
        <ListCell align="center">
          <FormattedDate value={row.stockingDate} day="2-digit" month="short" year="numeric" />
        </ListCell>
        <ListCell align="right">
          <FormattedNumber value={row.stockingWeight} minimumFractionDigits={0} maximumFractionDigits={0} />
        </ListCell>
        <ListCell align="right">
          <FormattedNumber value={(row.lastBiom / row.lastNum) * 1000} minimumFractionDigits={0} maximumFractionDigits={0} />
        </ListCell>
        <ListCell align="right">
          <FormattedNumber value={row.accEgi} minimumFractionDigits={0} maximumFractionDigits={0} />
        </ListCell>
        <ListCell align="right">
          <FormattedNumber value={row.accEfi} minimumFractionDigits={0} maximumFractionDigits={0} />
        </ListCell>
        <ListCell align={isMobile ? 'right' : 'center'}>
          <Box>{row.hasTemporary ? <CheckCircle color={theme.palette.darkBlue.main} size={22} /> : null}</Box>
        </ListCell>
        <ListCell align="center">
          {renderRecalculating(row)}
        </ListCell>
        <ListCell align="center" sx={{ width: { xs: '100%', md: 'auto' } }}>
          <Box display="flex" justifyContent={{ xs: 'space-between', md: 'flex-end' }} alignItems="center" sx={{ width: '100%' }}>
            <Button
              fullWidth={isMobile}
              size={'small'}
              onClick={() =>
                handleOnClick(`/planning-prognosis/sites/${params.siteTab}/${params.localSiteId}/${tab}/${row.fishGroupId}`)
              }>
              <FormattedMessage id="general.edit" values={{ type: '' }} />
            </Button>
          </Box>
        </ListCell>
      </ListRow>
    );
  };

  const renderActionGroup = (idx: number, extraBtn?: React.ReactNode) => {
    return (
      <ActionGroup
        sx={{
          mb: { xs: 3, md: 3 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 4,
        }}>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexWrap: isMobile ? 'wrap' : 'nowrap',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
          <Checkbox
            checked={selectedFishGroups.length === renderRows().length}
            label={intl.formatMessage({ id: 'general.selectAll' })}
            onChange={() =>
              selectedFishGroups.length === renderRows().length
                ? setSelectedFishGroups([])
                : setSelectedFishGroups(renderRows().map((s) => s.fishGroupId))
            }
          />
          <TextField
            hiddenLabel
            fullWidth={isMobile}
            label="search"
            placeholder={intl.formatMessage({ id: `general.search` })}
            value={searchValue}
            endAdornment={<Search size={18} />}
            onChange={(event) => {
              setSearchValue(event.target.value);
            }}
          />
        </Box>
        <Dialog
          ref={(el) => {
            if (el) profilesDialogRef.current[idx] = el;
          }}
          maxWidth="md"
          title={intl.formatMessage({ id: `${PAGE_NAME_ID}.editMultiple` })}
          button={
            <Button
              fullWidth={isMobile}
              disabled={account.company?.features.hasPlanningPrognosis && selectedFishGroups.length < 2}
              sx={{ minWidth: 80 }}
              onClick={() => checkMultiple()}>
              <FormattedMessage id="general.edit" values={{ type: '' }} />
            </Button>
          }>
          <form onSubmit={handleSubmit(handleSubmitProfile)}>
            {(updateDefaultProfile.isPending || resetDefaultProfiles.isPending) && <LoadingSpinner overlay />}
            <Grid container rowGap={6} spacing={4}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Box display="flex" gap={4} flexDirection={'column'}>
                  <Switch
                    dark
                    label={intl.formatMessage({ id: `${PAGE_NAME_ID}.accEgiLabel` })}
                    controller={{
                      control: control,
                      name: 'useEgiId',
                    }}
                  />
                  <Select
                    dark
                    fullWidth
                    label={''}
                    disabled={!values.useEgiId}
                    controller={{
                      control: control,
                      name: 'egiId',
                      rules: {
                        validate: {
                          invalidId: (v: number) => (values.useEgiId ? (v < 0 ? false : true) : true),
                        },
                      },
                    }}>
                    {profiles?.egi
                      .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
                      .map((profile) => (
                        <RadioSelectItem key={profile.id} value={profile.id} selected={profile.id === watch('egiId')}>
                          {profile.name}
                        </RadioSelectItem>
                      ))}
                  </Select>
                </Box>
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <Box display="flex" gap={4} flexDirection={'column'}>
                  <Switch
                    dark
                    label={intl.formatMessage({ id: `${PAGE_NAME_ID}.accEfiLabel` })}
                    controller={{
                      control: control,
                      name: 'useEfiId',
                    }}
                  />

                  <Select
                    dark
                    fullWidth
                    label={''}
                    disabled={!values.useEfiId}
                    controller={{
                      control: control,
                      name: 'efiId',
                      rules: {
                        validate: {
                          invalidId: (v: number) => (values.useEfiId ? (v < 0 ? false : true) : true),
                        },
                      },
                    }}>
                    {profiles?.efi
                      .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
                      .map((profile) => (
                        <RadioSelectItem key={profile.id} value={profile.id} selected={profile.id === watch('efiId')}>
                          {profile.name}
                        </RadioSelectItem>
                      ))}
                  </Select>
                </Box>
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <Box display="flex" gap={4} flexDirection={'column'}>
                  <Switch
                    dark
                    label={intl.formatMessage({ id: `${PAGE_NAME_ID}.mortality` })}
                    controller={{
                      control: control,
                      name: 'useMortalityId',
                    }}
                  />

                  <Select
                    dark
                    fullWidth
                    label={''}
                    disabled={!values.useMortalityId}
                    controller={{
                      control: control,
                      name: 'mortalityId',
                      rules: {
                        validate: {
                          invalidId: (v: number) => (values.useMortalityId ? (v < 0 ? false : true) : true),
                        },
                      },
                    }}>
                    {profiles?.mort
                      .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
                      .map((profile) => (
                        <RadioSelectItem key={profile.id} value={profile.id} selected={profile.id === watch('mortalityId')}>
                          {profile.name}
                        </RadioSelectItem>
                      ))}
                  </Select>
                </Box>
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <Box display="flex" gap={4} flexDirection={'column'}>
                  <Switch
                    dark
                    label={intl.formatMessage({ id: `${PAGE_NAME_ID}.feedStrategy` })}
                    controller={{
                      control: control,
                      name: 'useFeedStrategyId',
                    }}
                  />

                  <Select
                    dark
                    fullWidth
                    label={''}
                    disabled={!values.useFeedStrategyId}
                    controller={{
                      control: control,
                      name: 'feedStrategyId',
                      rules: {
                        validate: {
                          invalidId: (v: number) => (values.useFeedStrategyId ? (v < 0 ? false : true) : true),
                        },
                      },
                    }}>
                    {profiles?.feed
                      .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
                      .map((profile) => (
                        <RadioSelectItem key={profile.id} value={profile.id} selected={profile.id === watch('feedStrategyId')}>
                          {profile.name}
                        </RadioSelectItem>
                      ))}
                  </Select>
                </Box>
              </Grid>
              <Box display={'flex'} justifyContent={'end'} gap={4} width={'100%'}>
                <Button
                  disabled={values.useEfiId || values.useEgiId || values.useMortalityId || values.useFeedStrategyId}
                  type="submit"
                  onClick={handleResetProfiles}>
                  <FormattedMessage id="planningPrognosis.viewFishGroup.profileList.resetDefaultsBtn" />
                </Button>
                <Button type="submit">
                  <FormattedMessage id="general.save" />
                </Button>
              </Box>
            </Grid>
          </form>
        </Dialog>
        {extraBtn ?? null}
      </ActionGroup>
    );
  };

  // #endregion RENDERS

  // #region HOOKS

  useEffect(() => {
    let fishRows: FishGroupModel[] = [];

    switch (params.fishGroupTab) {
      case Tabs.FISH_GROUPS:
        fishRows = _.chain(fishGroups)
          .filter((fg) => fg.unitId !== '00000000-0000-0000-0000-000000000000')
          .value();
        break;
      case Tabs.VIRTUAL:
        fishRows = _.chain(fishGroups)
          .filter((fg) => fg.unitId === '00000000-0000-0000-0000-000000000000')
          .value();
        break;
      case Tabs.HARVESTED:
        fishRows = harvested || [];
        break;
      case Tabs.CALCULATOR:
        break;
      default:
        break;
    }

    setRows(_.sortBy(fishRows, (fg) => fg.unitName.toLowerCase()));
  }, [fishGroups, harvested, params.fishGroupTab]);

  useEffect(() => {
    return () => {
      handlePagination(1);
    };
  }, []);

  // #endregion HOOKS

  return (
    <>
      <FishGroupSubTabs />

      <Paper transparent marginBottom={0}>
        <TabPanel value={params.fishGroupTab} index={Tabs.FISH_GROUPS}>
          {renderActionGroup(1)}
          <List
            options={fishGroupsListOptions}
            orderBy="name"
            isLoading={isLoadingFishGroups}
            sort={setRows}
            rows={rows}
            noResult={{
              show: rows.length <= 0,
              text: <FormattedMessage id={`${PAGE_NAME_ID}.noResults`} />,
            }}>
            {renderRows()
              .slice(from, to)
              .map((row, idx) => renderFishgroup(row, idx, Tabs.FISH_GROUPS))}
          </List>
        </TabPanel>
        <TabPanel value={params.fishGroupTab} index={Tabs.VIRTUAL}>
          {renderActionGroup(
            2,
            <Dialog
              ref={createVirtualDialogRef}
              maxWidth="xs"
              title={intl.formatMessage({ id: `${PAGE_NAME_ID}.createVirtualFG` })}
              button={
                <Button fullWidth={isMobile} sx={{ minWidth: 80, marginTop: { xs: 4, md: 0 }, ml: { xs: 0, md: 2 } }}>
                  <FormattedMessage id="general.create" values={{ type: '' }} />
                </Button>
              }>
              <form
                onSubmit={handleSubmitVFG((data) => {
                  if (data.seaCloudStrainId === 0) {
                    delete data.seaCloudStrainId;
                  }
                  createVirtualFishgroup.mutate(data);
                })}>
                {createVirtualFishgroup.isPending && <LoadingSpinner overlay />}
                <FlexBox>
                  <DatePicker
                    fullWidth
                    dark
                    label={intl.formatMessage({ id: `planningPrognosis.createVirtualFishGroup.stockingDate` })}
                    controller={{
                      name: 'stockingDate',
                      control: controlVFG,
                      rules: { required: intl.formatMessage({ id: 'form.rules.required' }) },
                    }}
                  />
                  <TextField
                    fullWidth
                    dark
                    label={intl.formatMessage({ id: `planningPrognosis.createVirtualFishGroup.name` })}
                    controller={{
                      name: 'name',
                      control: controlVFG,
                      rules: { required: intl.formatMessage({ id: 'form.rules.required' }) },
                    }}
                  />
                  <TextField
                    fullWidth
                    dark
                    type="number"
                    label={intl.formatMessage({ id: `planningPrognosis.createVirtualFishGroup.numberOfFish` })}
                    controller={{
                      name: 'numberOfStockedFish',
                      control: controlVFG,
                      rules: { required: intl.formatMessage({ id: 'form.rules.required' }) },
                    }}
                  />
                  <TextField
                    fullWidth
                    dark
                    type="number"
                    label={intl.formatMessage({ id: `planningPrognosis.createVirtualFishGroup.stockingWeight` })}
                    controller={{
                      name: 'averageStockingWeight',
                      control: controlVFG,
                      rules: { required: intl.formatMessage({ id: 'form.rules.required' }) },
                    }}
                  />
                  <Select
                    fullWidth
                    dark
                    label={intl.formatMessage({ id: `planningPrognosis.createVirtualFishGroup.species` })}
                    controller={{
                      control: controlVFG,
                      name: 'speciesId',
                      rules: {
                        required: intl.formatMessage({ id: 'form.rules.required' }),
                        validate: {
                          invalidId: (v: number) => (v === 0 ? false : true),
                        },
                      },
                    }}
                    onChange={() => {
                      setValueVFG('smoltTypeId', 0);
                      setValueVFG('seaCloudStrainId', 0);
                    }}>
                    {species?.map((specie) => (
                      <Mui.MenuItem key={specie.id} value={specie.id}>
                        {specie.name}
                      </Mui.MenuItem>
                    ))}
                  </Select>
                  <Select
                    fullWidth
                    dark
                    label={intl.formatMessage({ id: `planningPrognosis.createVirtualFishGroup.smoltType` })}
                    controller={{
                      control: controlVFG,
                      name: 'smoltTypeId',
                      rules: {
                        required: intl.formatMessage({ id: 'form.rules.required' }),
                        validate: {
                          invalidId: (v: number) => (v === 0 ? false : true),
                        },
                      },
                    }}>
                    {smoltTypes
                      ?.filter((st) => st.speciesId === valuesVFG.speciesId)
                      .map((smoltType) => (
                        <Mui.MenuItem key={smoltType.id} value={smoltType.id}>
                          {smoltType.name}
                        </Mui.MenuItem>
                      ))}
                  </Select>
                  <Select
                    fullWidth
                    dark
                    label={intl.formatMessage({ id: `planningPrognosis.createVirtualFishGroup.strain` })}
                    controller={{
                      control: controlVFG,
                      name: 'seaCloudStrainId',
                    }}>
                    {strains
                      ?.filter((strain) => strain.speciesId === valuesVFG.speciesId)
                      .map((strain) => (
                        <Mui.MenuItem key={strain.id} value={strain.id}>
                          {strain.name}
                        </Mui.MenuItem>
                      ))}
                  </Select>
                </FlexBox>
                <Box display={'flex'} justifyContent={'end'}>
                  <Button type="submit" size="medium" sx={{ mt: 3 }}>
                    <FormattedMessage id="general.save" />
                  </Button>
                </Box>
              </form>
            </Dialog>
          )}
          <List
            options={fishGroupsListOptions}
            orderBy="name"
            isLoading={isLoadingFishGroups}
            sort={setRows}
            rows={rows}
            noResult={{
              show: rows.length <= 0,
              text: <FormattedMessage id={`${PAGE_NAME_ID}.noResults`} />,
            }}>
            {renderRows()
              .slice(from, to)
              .map((row, idx) => renderFishgroup(row, idx, Tabs.VIRTUAL))}
          </List>
        </TabPanel>
        <TabPanel value={params.fishGroupTab} index={Tabs.HARVESTED}>
          {params.fishGroupTab === Tabs.VIRTUAL && renderActionGroup(3)}
          <List
            options={fishGroupsListOptions}
            orderBy="name"
            isLoading={isLoadingHarvested}
            sort={setRows}
            rows={rows}
            noResult={{
              show: rows.length <= 0,
              text: <FormattedMessage id={`${PAGE_NAME_ID}.noResults`} />,
            }}>
            {renderRows()
              .slice(from, to)
              .map((row, idx) => renderFishgroup(row, idx, Tabs.HARVESTED))}
          </List>
        </TabPanel>
        <TabPanel value={params.fishGroupTab} index={Tabs.CALCULATOR}>
          <Calculator />
        </TabPanel>
        {account.company?.features.hasPlanningPrognosisManageCompanyOrSiteProfiles && (
          <TabPanel value={params.fishGroupTab} index={Tabs.PROFILES}>
            <EditSiteProfiles globalSiteId={globalSiteId} />
          </TabPanel>
        )}
        <Pagination currentPage={currentPage} pages={Math.ceil(renderRows().length / PAGINATION_LIMIT)} onClick={handlePagination} />
      </Paper>
    </>
  );
};

export default FishGroup;
